import { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";

// Loads the Google Identity Services script once. Cheap (~50KB cached
// across all visitors via Google's CDN).
function useGoogleScript(): boolean {
  const [ready, setReady] = useState(
    typeof window !== "undefined" && !!window.google?.accounts?.id,
  );
  useEffect(() => {
    if (ready) return;
    if (document.getElementById("gis-script")) return;
    const s = document.createElement("script");
    s.id = "gis-script";
    s.src = "https://accounts.google.com/gsi/client";
    s.async = true;
    s.defer = true;
    s.onload = () => setReady(true);
    document.head.appendChild(s);
  }, [ready]);
  return ready;
}

const CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;

export function SignInPage() {
  return <SignInScreen mode="sign-in" />;
}

export function SignUpPage() {
  return <SignInScreen mode="sign-up" />;
}

function SignInScreen({ mode }: { mode: "sign-in" | "sign-up" }) {
  const [, setLocation] = useLocation();
  const qc = useQueryClient();
  const ready = useGoogleScript();
  const btnRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!ready || !btnRef.current || !CLIENT_ID) return;
    const g = window.google?.accounts?.id;
    if (!g) return;
    g.initialize({
      client_id: CLIENT_ID,
      callback: async (resp: { credential?: string }) => {
        if (!resp.credential) {
          setError("No credential from Google");
          return;
        }
        try {
          await api("/auth/google", {
            method: "POST",
            body: { credential: resp.credential },
            public: true,
          });
          // /me has no active observer on the SignIn page, so a plain
          // invalidateQueries here only marks the cache stale — it does
          // NOT trigger a refetch. We need the cache populated before
          // navigating, otherwise SignedInOnly mounts on /dashboard,
          // sees stale-null data, and bounces back to /sign-in.
          await qc.refetchQueries({ queryKey: ["me"], type: "all" });
          setLocation("/dashboard");
        } catch (e) {
          setError((e as ApiError).message ?? "Sign-in failed");
        }
      },
      ux_mode: "popup",
      auto_select: false,
    });
    g.renderButton(btnRef.current, {
      type: "standard",
      theme: "outline",
      size: "large",
      shape: "pill",
      width: 280,
    });
  }, [ready, qc, setLocation]);

  return (
    <div className="min-h-dvh flex items-center justify-center bg-background px-4 py-12">
      <div className="max-w-sm w-full space-y-6 text-center">
        <div>
          <img
            src="/logo-cornholio.png"
            alt="Cornholio"
            width={216}
            height={38}
            className="h-10 w-auto inline-block mb-4"
          />
          <h1 className="text-3xl font-display uppercase tracking-tight">
            {mode === "sign-in" ? "Sign in" : "Get started"}
          </h1>
          <p className="text-muted-foreground text-sm mt-2">
            {mode === "sign-in"
              ? "Welcome back. Cornholio remembers."
              : "Your first sign-in creates your account."}
          </p>
        </div>

        {!CLIENT_ID ? (
          <p className="text-sm text-destructive">
            VITE_GOOGLE_CLIENT_ID is not set.
          </p>
        ) : (
          <div className="flex flex-col items-center gap-3">
            <div ref={btnRef} className="min-h-[44px]" />
            {!ready && (
              <p className="text-xs text-muted-foreground">Loading Google…</p>
            )}
          </div>
        )}

        {error && <p className="text-sm text-destructive">{error}</p>}

        <Button variant="ghost" onClick={() => setLocation("/")}>
          ← Back home
        </Button>
      </div>
    </div>
  );
}

// Minimal GIS typings — we only touch initialize + renderButton.
declare global {
  interface Window {
    google?: {
      accounts?: {
        id?: {
          initialize: (config: {
            client_id: string;
            callback: (resp: { credential?: string }) => void;
            ux_mode?: "popup" | "redirect";
            auto_select?: boolean;
          }) => void;
          renderButton: (
            el: HTMLElement,
            opts: {
              type?: "standard" | "icon";
              theme?: "outline" | "filled_blue" | "filled_black";
              size?: "large" | "medium" | "small";
              shape?: "rectangular" | "pill" | "circle" | "square";
              width?: number;
            },
          ) => void;
        };
      };
    };
  }
}
